import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import fullgoalLogoWhite from '../../imgs/fullgoal-logo-white.png';
import fgLogo from '../../imgs/fg_logo.png';
/**
 * 从登录 Presenter 导入重置密码和登录接口
 * forgetPasswordReset: 验证 token 并重置密码（接口文档 #9）
 * login: 重置成功后自动登录（接口文档 #1）
 */
import { forgetPasswordReset, login } from '../Login/LoginPresenter';
import { setItem, getItem } from '../../utils/localStorageUtil';
import { LOCAL_STORAGE_KEYS } from '../../const/localStorageConst';
import { setForceUpdatePassword } from '../../utils/forcePasswordUpdate';
import './ResetPwd.css';
import '../Login/Login.css';

const ResetPwd = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 从 URL query 参数中读取 email 和 token
  const emailParam = searchParams.get('email') || '';
  const tokenParam = searchParams.get('token') || '';

  const [email, setEmail] = useState(emailParam);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  // 页面挂载时用 URL 参数预填 email
  useEffect(() => {
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [emailParam]);

  const canSubmit = Boolean(
    email.trim() &&
    newPassword.trim() &&
    confirmPassword.trim() &&
    newPassword === confirmPassword &&
    tokenParam
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit || loading) {
      return;
    }

    if (newPassword !== confirmPassword) {
      message.warning('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    try {
      // 1. 调用重置密码接口（接口文档 #9）
      const resetRes = await forgetPasswordReset({
        email_account: email.trim(),
        token: tokenParam,
        update_pwd: newPassword,
      });

      if (!resetRes || resetRes.status !== 200) {
        message.error(resetRes?.msg || '重置密码失败，链接可能已过期');
        setLoading(false);
        return;
      }

      message.success('密码重置成功，正在自动登录...');

      // 2. 重置成功后自动调用登录接口
      const loginRes = await login({
        username: email.trim(),
        pwd: newPassword,
      });

      if (loginRes && loginRes.status === 200) {
        // 登录成功 — 保存账号密码到 localStorage
        setItem(LOCAL_STORAGE_KEYS.LOGIN_USERNAME, email.trim());
        setItem(LOCAL_STORAGE_KEYS.LOGIN_PWD, newPassword);

        // 保存 token、用户信息和强制修改密码标记
        setItem(LOCAL_STORAGE_KEYS.TOKEN, loginRes.token);
        setItem(LOCAL_STORAGE_KEYS.IS_ADMIN, loginRes.is_admin);
        setItem(LOCAL_STORAGE_KEYS.USER_INFO, {
          username: email.trim(),
          is_admin: loginRes.is_admin,
        });
        setForceUpdatePassword(loginRes.force_update_password ?? '1');

        // 跳转到对应页面
        const redirectPath = loginRes.is_admin ? '/admin-dashboard' : '/user-dashboard';
        navigate(redirectPath, { replace: true });
      } else {
        message.success('密码已重置，请返回登录页使用新密码登录');
        navigate('/login', { replace: true });
      }
    } catch (err) {
      message.error('请求失败，请检查网络后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-pwd-page">
      {/* 背景装饰 */}
      <div className="login-page__background" />
      <div className="login-page__grid" />
      <div className="login-page__aurora login-page__aurora--left" />
      <div className="login-page__aurora login-page__aurora--right" />

      {/* 左上角品牌标识 */}
      <img src={fgLogo} alt="富小助" className="login-page__brand-corner" />

      <div className="reset-pwd-page__content">
        <div className="login-card" style={{ maxWidth: '28rem', margin: '0 auto' }}>
          {/* 卡片顶部品牌区 */}
          <div className="login-card__brand">
            <img src={fgLogo} alt="富国基金" className="login-card__logo" />
            <div>
              <div className="login-card__brand-name">富小助平台</div>
              <div className="login-card__brand-tagline">连接你的专属 AI 助手</div>
            </div>
          </div>

          {/* 标题区 */}
          <div className="login-card__header">
            <h2>重置密码</h2>
            <p>请设置您的新登录密码</p>
          </div>

          {/* 重置密码表单 */}
          <form className="login-form" onSubmit={handleSubmit}>
            <label className="login-form__field">
              <span>邮箱账号</span>
              <input
                type="email"
                value={email}
                disabled
                placeholder="邮箱地址"
                autoComplete="email"
              />
            </label>

            <label className="login-form__field">
              <span>新密码</span>
              <div className="login-form__password-wrapper">
                <input
                  type={showNewPwd ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  placeholder="请输入新密码"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="login-form__toggle"
                  onClick={() => setShowNewPwd((current) => !current)}
                >
                  {showNewPwd ? '隐藏' : '显示'}
                </button>
              </div>
            </label>

            <label className="login-form__field">
              <span>确认新密码</span>
              <div className="login-form__password-wrapper">
                <input
                  type={showConfirmPwd ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="请再次输入新密码"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="login-form__toggle"
                  onClick={() => setShowConfirmPwd((current) => !current)}
                >
                  {showConfirmPwd ? '隐藏' : '显示'}
                </button>
              </div>
            </label>

            {/* 密码不匹配提示 */}
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <div style={{ color: '#f87171', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                两次输入的密码不一致
              </div>
            )}

            <button
              type="submit"
              className="login-form__submit"
              disabled={!canSubmit || loading}
            >
              <span>{loading ? '提交中...' : '重置密码'}</span>
              {loading && <span className="login-form__submit-loading" />}
            </button>

            <button
              type="button"
              className="login-form__back-link"
              onClick={() => navigate('/login', { replace: true })}
            >
              ← 返回登录
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPwd;
